import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Scale, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EstimateWidget from '../components/EstimateWidget';
import { Button } from '../components/ui/button';
import SEO from '../seo/SEO';
import { COMPETITORS } from '../data/competitors';

export default function CompareIndex() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="3PL Comparisons — Shipmaster vs ShipBob, ShipHero, ShipMonk & More"
        description="Honest side-by-side comparisons of Shipmaster Fulfillment against ShipBob, ShipHero, ShipMonk, Red Stag, and Easyship. Pricing, minimums, onboarding, and where each platform wins."
        keywords="3PL comparison, ShipBob alternative, ShipHero alternative, ShipMonk alternative, Red Stag alternative, Easyship alternative, 3PL alternatives"
      />
      <Header />
      <main>
        {/* Hero */}
        <section
          className="relative overflow-hidden text-white"
          style={{
            background:
              'linear-gradient(135deg, #1e1b4b 0%, #312e81 35%, #4f46e5 100%)',
          }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs font-bold uppercase tracking-widest">
              <Scale className="w-3.5 h-3.5" /> 3PL Comparisons
            </div>
            <h1 className="mt-5 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              How we compare to other 3PLs
            </h1>
            <p className="mt-5 max-w-2xl text-base sm:text-lg text-indigo-100 leading-relaxed">
              Picking a fulfillment partner is a high-cost decision to undo. We
              put together honest side-by-side comparisons against the major
              players so you can decide on facts, not marketing pitches.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="lg"
                className="bg-white text-indigo-700 hover:bg-indigo-50 rounded-full font-bold h-12 px-7 shadow-lg"
                data-testid="compare-index-hero-cta"
              >
                <Link to="/#contact">Get a Free Estimate</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Competitor grid */}
        <section className="py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
              Pick your comparison
            </p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              Currently considering one of these?
            </h2>
            <p className="mt-3 max-w-2xl text-slate-600 leading-relaxed">
              Click into any comparison for the full breakdown — pricing,
              policies, where each platform wins, and what migration looks like.
            </p>

            <ul className="mt-10 grid sm:grid-cols-2 gap-5">
              {COMPETITORS.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/compare/${c.slug}`}
                    className="group block p-7 rounded-2xl bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all"
                    data-testid={`compare-index-${c.slug}`}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-extrabold tracking-tight text-slate-900 group-hover:text-indigo-700">
                        Shipmaster vs {c.name}
                      </h3>
                      <ArrowRight className="w-5 h-5 text-indigo-500 transform group-hover:translate-x-1 transition-transform" />
                    </div>
                    <p className="mt-2 text-sm font-semibold text-indigo-700">
                      {c.tagline}
                    </p>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                      {c.blurb}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Why brands switch — generic value prop */}
        <section className="py-14 sm:py-20 bg-slate-50/60 border-y border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-1">
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                Why brands switch to Shipmaster
              </p>
              <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
                The recurring themes
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                These are the same reasons that come up across every migration
                conversation we have. If you&apos;re feeling any of these,
                you&apos;re probably overdue for a switch.
              </p>
            </div>
            <ul className="lg:col-span-2 space-y-4">
              {[
                'Tired of opaque pricing and surprise line items at month-end',
                'Sick of ticket-queue support when something needs a real human now',
                'Onboarding promised in days, took months',
                'Account managers rotate every 6 months — no continuity',
                'Hidden fees: pick-after-first, software, platform, kitting surcharges',
                'Slow to ship same-day even though SLA says they should',
              ].map((reason) => (
                <li
                  key={reason}
                  className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-slate-200"
                >
                  <CheckCircle2
                    className="flex-shrink-0 w-5 h-5 text-indigo-600 mt-0.5"
                    strokeWidth={2.5}
                  />
                  <span className="text-sm font-medium text-slate-800 leading-snug">
                    {reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA — embedded estimate widget */}
        <section className="py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">
                Free instant estimate
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
                Run the math against your current 3PL
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Drop your store URL and monthly order volume — we&apos;ll show
                you a real-time savings estimate based on your actual lane.
              </p>
            </div>
            <div className="mt-10 max-w-3xl mx-auto">
              <EstimateWidget />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
